import React from 'react';
import { FOOTER } from '../data/mock';
import { Link } from 'react-router-dom';
import { Mail, Instagram } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="relative border-t border-[#3a2818]/60 pt-16 pb-10 px-6 md:px-10">
      {/* Presented by COLAB — hero credit */}
      <div className="max-w-6xl mx-auto flex flex-col items-center text-center mb-14">
        <p className="text-[10px] tracking-[0.5em] font-fell-sc text-[#8b7355] uppercase mb-5">
          A COLAB Theatre Production
        </p>
        <a
          href="https://www.colabtheatre.co.uk/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="COLAB Theatre — visit website"
          className="group inline-block"
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/ash/colab-logo.jpg"
            aria-label="COLAB Theatre — Immersive Theatre London"
            className="w-64 md:w-80 aspect-video object-cover transition-all duration-500 group-hover:scale-105 group-hover:brightness-110 drop-shadow-[0_0_40px_rgba(220,150,70,0.18)]"
          >
            <source src="/ash/colab-logo.webm" type="video/webm" />
            <source src="/ash/colab-logo.mp4" type="video/mp4" />
            <img
              src="/ash/colab-logo.jpg"
              alt="COLAB Theatre — Immersive Theatre London"
              className="w-full h-full object-cover"
            />
          </video>
        </a>
        <a
          href="https://www.colabtheatre.co.uk/"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 text-[11px] tracking-[0.4em] font-fell-sc text-[#c8935a] hover:text-[#e8dcc0] transition-colors uppercase"
        >
          colabtheatre.co.uk
        </a>
      </div>

      {/* Divider */}
      <div className="max-w-6xl mx-auto divider-ornament mb-12">
        <span className="text-[9px] tracking-[0.4em] font-fell-sc">†</span>
      </div>

      <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10 items-start">
        <div>
          <p className="font-display text-2xl tracking-[0.35em] text-[#e8dcc0]">
            ASH
          </p>
          <p className="mt-2 text-[10px] tracking-[0.3em] font-fell-sc text-[#8b7355]">
            THE WITCH TRIALS OF DRUDENHAUS
          </p>
          <p className="mt-4 font-fell italic text-xs text-[#8b7355] leading-relaxed max-w-[240px]">
            London's most terrifying immersive Halloween theatre — a scary,
            intimate witch trial at COLAB Theatre, London Bridge, 2026.
          </p>
        </div>

        <div className="font-fell text-sm text-[#a89880] leading-relaxed">
          <p>{FOOTER.venue}</p>
          <a
            href={`mailto:${FOOTER.email}`}
            className="mt-4 inline-flex items-center gap-2 text-[#c8935a] hover:text-[#e8dcc0] transition-colors text-sm"
          >
            <Mail className="w-3.5 h-3.5" />
            {FOOTER.email}
          </a>
        </div>

        <div className="md:text-right">
          <a
            href="https://www.instagram.com/colabtheatre"
            target="_blank"
            rel="noopener noreferrer"
            title="COLAB Theatre on Instagram — behind the scenes of London's best immersive Halloween shows"
            className="inline-flex items-center gap-2 text-[#a89880] hover:text-[#c8935a] transition-colors text-sm font-fell-sc tracking-[0.3em] uppercase"
          >
            <Instagram className="w-3.5 h-3.5" />
            @colabtheatre
          </a>
        </div>
      </div>

      {/* Related & Elsewhere — keyword-rich backlinks */}
      <nav
        aria-label="Related COLAB Theatre pages and immersive Halloween theatre resources"
        className="max-w-6xl mx-auto mt-14 pt-10 border-t border-[#3a2818]/40 grid md:grid-cols-3 gap-8"
      >
        <div>
          <p className="text-[10px] tracking-[0.4em] font-fell-sc text-[#c8935a] uppercase mb-4">
            The Show
          </p>
          <ul className="space-y-2.5 font-fell text-sm text-[#a89880]">
            <li>
              <a
                href="/#top"
                title="ASH — the best immersive Halloween theatre in London 2026"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Best Immersive Halloween Theatre 2026
              </a>
            </li>
            <li>
              <a
                href="/#what"
                title="Face the Inquisitor at London's most terrifying immersive experience"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Most Terrifying Immersive Experience
              </a>
            </li>
            <li>
              <a
                href="/#history"
                title="Rooted in real history — the Bamberg witch trials of 1627"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Real Witch-Trial History
              </a>
            </li>
            <li>
              <a
                href="/#book"
                title="Book the scariest immersive Halloween show in London 2026"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Book the Scariest Halloween Show
              </a>
            </li>
            <li>
              <Link
                to="/press"
                data-testid="footer-press-link"
                title="Press release, image pack and ticketing blurbs — ASH immersive Halloween theatre"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Press Kit & Image Pack
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-[10px] tracking-[0.4em] font-fell-sc text-[#c8935a] uppercase mb-4">
            COLAB Theatre
          </p>
          <ul className="space-y-2.5 font-fell text-sm text-[#a89880]">
            <li>
              <a
                href="https://www.colabtheatre.co.uk/"
                target="_blank"
                rel="noopener"
                title="COLAB Theatre — leaders of immersive theatre in London"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Best Immersive Theatre Company in London
              </a>
            </li>
            <li>
              <a
                href="https://www.colabtheatre.co.uk/whatson"
                target="_blank"
                rel="noopener"
                title="What's on at COLAB Theatre — most immersive shows in London"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                What's On — Most Immersive Shows
              </a>
            </li>
            <li>
              <a
                href="https://www.colabtheatre.co.uk/venues"
                target="_blank"
                rel="noopener"
                title="COLAB Venues — immersive theatre spaces in London"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Immersive Venues, London Bridge
              </a>
            </li>
            <li>
              <a
                href="https://www.colabtheatrereviews.co.uk/"
                target="_blank"
                rel="noopener"
                title="Read reviews of London's best immersive Halloween theatre"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Reviews — Best Immersive Theatre
              </a>
            </li>
          </ul>
        </div>

        <div>
          <p className="text-[10px] tracking-[0.4em] font-fell-sc text-[#c8935a] uppercase mb-4">
            Halloween in London
          </p>
          <ul className="space-y-2.5 font-fell text-sm text-[#a89880]">
            <li>
              <a
                href="/#book"
                title="Scary Halloween theatre in London Bridge 2026"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Scary Halloween Theatre London
              </a>
            </li>
            <li>
              <a
                href="/#where"
                title="The Cottage — Tudor immersive theatre venue London Bridge"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Immersive Halloween Venue London Bridge
              </a>
            </li>
            <li>
              <a
                href="/#book"
                title="Most immersive Halloween experience in London 2026"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Most Immersive Halloween Experience
              </a>
            </li>
            <li>
              <a
                href={`mailto:${FOOTER.email}?subject=Private%20group%20hire%20%E2%80%94%20ASH%20immersive%20Halloween%20theatre`}
                title="Book a private group at the best Halloween immersive show in London"
                className="hover:text-[#e8dcc0] transition-colors"
              >
                Private Group Halloween Hire
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* SEO description strip */}
      <p className="max-w-4xl mx-auto mt-12 text-center font-fell italic text-xs text-[#5a4d3a] leading-relaxed">
        ASH — The Witch Trials of Drudenhaus is the best, most terrifying and
        most intimate immersive Halloween theatre experience in London 2026.
        Produced by{' '}
        <a
          href="https://www.colabtheatre.co.uk/"
          target="_blank"
          rel="noopener"
          className="underline decoration-dotted underline-offset-4 hover:text-[#c8935a]"
        >
          COLAB Theatre
        </a>{' '}
        — leaders of scary immersive theatre in London Bridge — at{' '}
        <a
          href="/#where"
          className="underline decoration-dotted underline-offset-4 hover:text-[#c8935a]"
        >
          The Cottage
        </a>
        , a Tudor house on Borough High Street. If you're searching for the
        best scary Halloween show, the most immersive Halloween experience or
        the most terrifying immersive theatre in London this October and
        November, this is it.
      </p>

      <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-[#3a2818]/40 text-center">
        <p className="text-[10px] tracking-[0.3em] font-fell-sc text-[#5a4d3a] uppercase">
          {FOOTER.copyright}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
